import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

export default function ClientProjectView({ projectId }) {
  const [project, setProject] = useState(null);
  const [metricDefs, setMetricDefs] = useState([]);
  const [latest, setLatest] = useState({});
  const [team, setTeam] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function load() {
    setLoading(true);

    const { data: proj } = await supabase
      .from("projects")
      .select("*, project_types(name)")
      .eq("id", projectId)
      .single();
    setProject(proj);

    const { data: allMetrics } = await supabase.from("master_metrics").select("*");
    const defs = (proj?.metric_keys || []).map(
      (key) => (allMetrics || []).find((m) => m.key === key) || { key, display_name: key, unit: "count" }
    );
    setMetricDefs(defs);

    const thisMonth = new Date().toISOString().slice(0, 7);
    const { data: monthRows } = await supabase
      .from("metrics")
      .select("*")
      .eq("project_id", projectId)
      .gte("date", `${thisMonth}-01`);

    const totals = {};
    (monthRows || []).forEach((row) => {
      totals[row.metric_key] = (totals[row.metric_key] || 0) + Number(row.value);
    });
    setLatest(totals);

    const { data: teamRows } = await supabase
      .from("project_team_members")
      .select("*")
      .eq("project_id", projectId)
      .eq("visible_to_client", true);
    setTeam(teamRows || []);

    const { data: noteRows } = await supabase
      .from("project_notes")
      .select("*")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false })
      .limit(5);
    setNotes(noteRows || []);

    setLoading(false);
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;
  if (!project) return <p className="text-sm text-destructive">Project not found.</p>;

  const goalTargets = project.goal_targets || {};

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <h2 className="text-base font-semibold text-foreground">{project.name}</h2>
        <span className="font-mono text-[10.5px] bg-accent text-foreground rounded-full px-2.5 py-1">
          {project.project_types?.name || "Custom"}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">This month so far</p>

      <div className="grid grid-cols-4 gap-4 mb-5">
        {metricDefs.map((m) => (
          <div key={m.key} className="border border-border rounded-2xl bg-card p-4">
            <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
              {m.display_name}
            </div>
            <div className="text-lg font-semibold text-foreground">
              {formatValue(latest[m.key], m.unit)}
            </div>
          </div>
        ))}
      </div>

      {Object.keys(goalTargets).length > 0 && (
        <div className="border border-border rounded-2xl bg-card p-5 mb-5">
          <div className="text-sm font-semibold text-foreground mb-3">Goal vs actual</div>
          {metricDefs
            .filter((m) => goalTargets[m.key])
            .map((m) => {
              const actual = latest[m.key] || 0;
              const target = Number(goalTargets[m.key]);
              const pct = target > 0 ? Math.min(100, Math.round((actual / target) * 100)) : 0;
              return (
                <div key={m.key} className="mb-3 last:mb-0">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium">{m.display_name}</span>
                    <span className="font-mono text-muted-foreground">
                      {formatValue(actual, m.unit)} / {formatValue(target, m.unit)} target
                    </span>
                  </div>
                  <div className="h-2 bg-accent rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${pct >= 100 ? "bg-foreground" : "bg-primary"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="border border-border rounded-2xl bg-card overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">
            Your team
          </div>
          {team.length === 0 ? (
            <p className="text-sm text-muted-foreground p-5">Not assigned yet.</p>
          ) : (
            team.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-5 py-3 border-b border-border last:border-0">
                <div className="w-8 h-8 rounded-full bg-accent text-foreground flex items-center justify-center text-xs font-semibold">
                  {(t.member_name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-medium">{t.member_name}</div>
                  <div className="text-xs text-muted-foreground">{t.title}</div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border border-border rounded-2xl bg-card overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">
            Project notes
          </div>
          {notes.length === 0 ? (
            <p className="text-sm text-muted-foreground p-5">No updates yet.</p>
          ) : (
            <div className="p-5">
              {notes.map((n) => (
                <div key={n.id} className="py-2 border-b border-border last:border-0 text-sm">
                  <div className="font-mono text-[11px] text-muted-foreground mb-0.5">
                    {new Date(n.created_at).toLocaleDateString()}
                  </div>
                  {n.note}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatValue(value, unit) {
  const v = value || 0;
  if (unit === "currency") return `₹${Number(v).toLocaleString("en-IN")}`;
  if (unit === "percentage") return `${v}%`;
  if (unit === "ratio") return `${v}x`;
  return Number(v).toLocaleString("en-IN");
}
